import React, { useEffect, useRef, useState } from 'react';
import { Viewer, Worker } from '@react-pdf-viewer/core';
import { PDF_WORKER_URL } from '@/config/pdfViewer';
import { zoomPlugin } from '@react-pdf-viewer/zoom';
import '@react-pdf-viewer/core/lib/styles/index.css';
import '@react-pdf-viewer/zoom/lib/styles/index.css';
import Moveable from 'react-moveable';
import { useDropzone } from 'react-dropzone';
import ReactDOM from 'react-dom';
import { Alert, KeenIcon, DefaultTooltip } from '@/components';
import {
  Signature,
  CaseUpper,
  Type,
  ChevronsUpDown,
  CheckCircle,
  CheckSquare,
  BadgeInfo,
  Building2,
  Mail,
  Calendar1,
  ALargeSmall
} from 'lucide-react';
import { FormatFileSize, toSnake } from '@/utils';
import { RequestFileUploadModal } from '@/components/modal';
import * as Yup from 'yup';
import { useAuthContext } from '@/auth';
import eSignatureService from '@/services/e-signature/eSignatureService';
import { combineErrorMessage, packetName, packetTooLargeError, pdfSelectionError } from './esignPacket';
import {
  RECIPIENT_KEY,
  moveSigner,
  newSignerKey,
  recipientSigner,
  signerColor,
  signerDisplay,
  signerFieldCounts,
  signerPayload,
  signerProblem,
} from './esignSigners';
import { EsignSignersBar } from './EsignSignersBar';

// const MAX_SIZE = 2 * 1024 * 1024; // 2 MB: restrict file size
const MAX_SIZE = 20 * 1024 * 1024; // 20 MB: restrict file size

// Available field types that user can place on PDF
// "label" = human-readable name, "badge" = small tag shown on overlay
const FIELD_TYPES = {
  signature: { label: 'Signature', badge: 'Signature' },
  initials: { label: 'Initials', badge: 'Initials' },
  text: { label: 'Textbox', badge: 'Text' },
  checkbox: { label: 'Checkbox', badge: 'Checkbox' },
  radio: { label: 'Radio', badge: 'Radio' },
  dropdown: { label: 'Dropdown', badge: 'Dropdown' },
  date_signed: { label: 'Date Signed', badge: 'Date' },
  full_name: { label: 'Full Name', badge: 'Name' },
  email: { label: 'Email Address', badge: 'Email' },
  company: { label: 'Company', badge: 'Company' },
};

// Default size (in pixels) when a new field is placed on a page
// Chosen so fields don’t overlap and are easy to grab/move
const DEFAULT_RECT_BY_TYPE = {
  signature: { w: 180, h: 50 },
  initials: { w: 127, h: 127 },
  text: { w: 200, h: 80 },
  checkbox: { w: 28, h: 28 },
  radio: { w: 28, h: 28 },
  dropdown: { w: 200, h: 90 },

  date_signed: { w: 160, h: 40 },
  full_name: { w: 200, h: 40 },
  email: { w: 220, h: 40 },
  company: { w: 220, h: 40 },
};

// Utility: returns today’s date in YYYY-MM-DD format
// Used for “date signed” auto-fill option
const yyyymmdd = (d = new Date()) => {
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

// Utility: generate a short random string (used for checkbox/radio option IDs)
const uid = () => Math.random().toString(36).slice(2, 9);
const SIGNABLE_FIELD_TYPES = new Set(['signature', 'initials']);
/**
 * DocumentUploadModal
 * 
 * Main modal component for:
 * - Uploading a PDF
 * - Previewing it
 * - Placing fields (signature, text, etc.)
 * - Editing field properties
 * - Sending final document for signature
 */
const DocumentUploadModal = ({
  show,
  onClose,                // Callback when modal is closed
  onConfirm,              // Callback when user submits
  title = 'Upload Document',
  module,                 // Context: "contact" or "matter"
  Data,                   // Recipient info (varies by module)
  loading,
  errors = {},            // Errors passed from parent (server/API)
  hideParentOnFileUplaod, // Optional: hide parent modal when file uploaded
  resetChildState,         // Optional: signal to reset modal from outside
  forMultipleContacts = false,
  initialFile = null,      // Optional: a File to open with (skips the drop zone) — demand letters, OL-124
}) => {
  console.log('DATA', Data)
  const { auth } = useAuthContext();
  // Viewer reference for attaching MutationObserver and event listeners
  const viewerRef = useRef(null);

  // PDF handling states
  const [pdfUrl, setPdfUrl] = useState(null);
  const [uploadedFile, setUploadedFile] = useState(null);

  // Field handling states
  const [boxes, setBoxes] = useState([]);
  // const [targetRefs, setTargetRefs] = useState({});
  const targetRefs = useRef({});
  const [selectedBoxId, setSelectedBoxId] = useState(null);
  const [currentTool, setCurrentTool] = useState('signature');
  const [currentScale, setCurrentScale] = useState(1);

  // Zoom plugin from react-pdf-viewer
  const zoomPluginInstance = zoomPlugin();
  const { ZoomIn, ZoomOut, CurrentScale } = zoomPluginInstance;

  // Determine contact depending on whether module = matter or contact
  const contact = module === 'matter' ? Data?.contact : Data;
  const contactFullName = [contact?.first_name, contact?.middle_name || '', contact?.last_name]
    .filter(Boolean)
    .join(' ');

  // Local UI state (property panel toggle, nested modal, errors)
  const [togglePropertyPanel, setTogglePropertyPanel] = useState(false)
  const [docUploadModal, setDocUploadModal] = useState(false)
  const [docUploadLoading, setDocUploadLoading] = useState(false)
  const [uiErrors, setUiErrors] = useState({});
  const [dropAreaError, setDropAreaError] = useState('');
  const [combiningCount, setCombiningCount] = useState(0);
  const [signerOptions, setSignerOptions] = useState([]);
  // OL-203: who signs, in order. Signer 1 starts as the contact (or, when
  // sending to many, each contact in turn).
  const [signers, setSigners] = useState(() => [recipientSigner()]);
  const [activeSignerKey, setActiveSignerKey] = useState(RECIPIENT_KEY);

  const addSigner = ({ name, email }) => {
    const key = newSignerKey();
    setSigners((prev) => [...prev, { key, role: 'person', name, email }]);
    setActiveSignerKey(key);
    setUiErrors({});
  };

  const updateSigner = (key, values) => {
    setSigners((prev) => prev.map((s) => (s.key === key ? { ...s, ...values } : s)));
    setUiErrors({});
  };

  const removeSigner = (key) => {
    const fieldCount = boxes.filter((b) => b.signer_key === key).length;
    if (fieldCount && !window.confirm(`Remove this signer and their ${fieldCount} ${fieldCount === 1 ? 'field' : 'fields'}?`)) {
      return;
    }
    const remaining = signers.filter((s) => s.key !== key);
    setSigners(remaining);
    setBoxes((prev) => prev.filter((b) => b.signer_key !== key));
    if (activeSignerKey === key) setActiveSignerKey(remaining[0]?.key);
    setSelectedBoxId(null);
    setUiErrors({});
  };

  const validationSchema = Yup.object().shape({
    contact_id: forMultipleContacts
      ? Yup.array()
        .of(
          Yup.string().required('Contact is required')
        ).min(1, 'At least one contact must be assigned')
        .required('Contact is required')
      : Yup.array().notRequired(),
    subject: Yup.string().required("Subject is required").max(100),
    message: Yup.string()
      .max(1000, 'Message cannot exceed 1000 characters'),
    due_date: Yup.string().nullable(),
  });

  const moduleEntityId = module === 'matter' ? Data?.contact?.id : Data?.id;

  const normalizeSignerOption = (signer, index) => {
    const firstName = signer?.first_name || signer?.firstName || '';
    const middleName = signer?.middle_name || signer?.middleName || '';
    const lastName = signer?.last_name || signer?.lastName || '';
    const nameFromParts = [firstName, middleName, lastName].filter(Boolean).join(' ').trim();
    const name = signer?.full_name || signer?.fullName || signer?.name || nameFromParts || '';
    const email = signer?.email || signer?.recipient_email || signer?.signer_email || '';
    const id =
      signer?.uuid ||
      signer?.id ||
      signer?.recipient_id ||
      signer?.signer_id ||
      signer?.signer_uuid ||
      email ||
      `signer-${index + 1}`;

    return {
      value: String(id),
      label: name,
      // label: name && email ? `${name} (${email})` : (name || email || `Signer ${index + 1}`),
      email,
      signer_id: String(id),
      signer_name: name,
      signer_email: email,
    };
  };

  const resolveSignerList = (response) => {
    if (Array.isArray(response)) return response;
    if (Array.isArray(response?.data)) return response.data;
    if (Array.isArray(response?.signers)) return response.signers;
    if (Array.isArray(response?.data?.signers)) return response.data.signers;
    return [];
  };

  // Utility: normalizes recipient data from API response
  // Why? Because API may return inconsistent keys (firstName vs first_name)
  const resolveRecipient = (Data) => {
    const d = Data || {};
    const contact = d.contact || d;

    const first = contact.first_name || contact.firstName || '';
    const last = contact.last_name || contact.lastName || '';
    const fullFromParts = [first, last].filter(Boolean).join(' ');
    const full =
      contact.full_name || contact.fullName || contact.name || fullFromParts;

    return {
      fullName: full || '',
      email: contact.email || '',
      company: contact.company_name || auth?.user?.company_name || '',
      // title: r.title || contact.title || '',
    };
  };


  /**
   * Reset modal completely when parent passes resetChildState flag
   * Use case: when parent closes everything after successful submission
   */
  useEffect(() => {
    if (resetChildState) {
      setDocUploadModal(false);
      handleCloseModal()
      removeFile()
      onClose();
    }
  }, [resetChildState]);

  /**
   * Observe PDF viewer DOM for page virtualization changes.
   * Why? react-pdf-viewer only renders pages in viewport.
   * When new pages mount/unmount, we need to re-render fields (boxes).
   */
  useEffect(() => {
    const container = viewerRef.current;
    if (!container) return;

    const observer = new MutationObserver(() => {
      setBoxes((prev) => [...prev]);
    });

    observer.observe(container, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, [pdfUrl]);

  useEffect(() => {
    const fetchRecipientSigners = async () => {
      if (!show || !module || !moduleEntityId) {
        setSignerOptions([]);
        return;
      }

      try {
        const response = await eSignatureService.getRecipientSigners('contact', moduleEntityId);
        const options = resolveSignerList(response).map((signer, index) =>
          normalizeSignerOption(signer, index)
        );
        setSignerOptions(options);
      } catch (error) {
        console.error('Unable to fetch recipient signers:', error);
        setSignerOptions([]);
      }
    };

    fetchRecipientSigners();
  }, [show, module, moduleEntityId]);


  const togglePropertyPannel = () => {
    setTogglePropertyPanel(prev => !prev)
  }

  const clearAllLocalErrors = () => {
    setDropAreaError('');
    setUiErrors({});
  };

  const acceptAndPreview = (file) => {
    console.log('file', file);
    if (file) {
      // hide parent modal upon file upload (for now only for accessing from dashboard flow)
      hideParentOnFileUplaod?.()
    }

    const reader = new FileReader();
    reader.onload = () => {
      setUploadedFile(file);
      setPdfUrl(reader.result);
    };
    reader.readAsDataURL(file);
  };

  // OL-150: several PDFs are combined on the server into one signing packet,
  // so fields are placed once and the signers sign once.
  const handleSelectedFiles = async (files) => {
    if (!files.length || combiningCount) return;

    const error = pdfSelectionError(files, MAX_SIZE);
    if (error) {
      setDropAreaError(error);
      setUploadedFile(null);
      setPdfUrl(null);
      return;
    }

    setDropAreaError('');
    if (files.length === 1) {
      acceptAndPreview(files[0]);
      return;
    }

    setCombiningCount(files.length);
    try {
      const packet = await eSignatureService.combineDocuments(files);
      const tooLarge = packetTooLargeError(packet.size, MAX_SIZE);
      if (tooLarge) {
        setDropAreaError(tooLarge);
        return;
      }
      acceptAndPreview(new File([packet], packetName(files), { type: 'application/pdf' }));
    } catch (err) {
      setDropAreaError(await combineErrorMessage(err));
    } finally {
      setCombiningCount(0);
    }
  };

  // A caller can hand us the PDF (a generated demand letter); load it as if
  // it had been dropped so the builder opens straight on the field placement.
  useEffect(() => {
    if (show && initialFile && !uploadedFile) {
      acceptAndPreview(initialFile);
    }
  }, [show, initialFile]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: { 'application/pdf': ['.pdf'] },
    multiple: true,
    maxSize: MAX_SIZE,
    disabled: combiningCount > 0,
    onDrop: (acceptedFiles, fileRejections) => {
      // onDropRejected reports the problem; don't combine only part of what was dropped.
      if (fileRejections?.length) return;
      handleSelectedFiles(acceptedFiles || []);
    },
    onDropRejected: (fileRejections) => {
      const first = fileRejections?.[0];
      const file = first?.file;
      const err = first?.errors?.[0];

      if (err?.code === 'file-too-large') {
        setDropAreaError(
          `File too large (${FormatFileSize(file?.size || 0)}). Max allowed is ${FormatFileSize(MAX_SIZE)}.`
        );
      } else if (err?.code === 'file-invalid-type') {
        setDropAreaError('Invalid file type. Only PDF files are supported.');
      } else {
        setDropAreaError(`Unable to add file. Please try again with a valid PDF under ${FormatFileSize(MAX_SIZE)}.`);
      }
      setUploadedFile(null);
      setPdfUrl(null);
    },
  });

  /**
   * Resolve the pre-fill value for a standalone auto field (full_name, email,
   * company). The signer sees it as an editable text field and can overwrite it.
   */
  const getAutoValue = (id, recipientObj) => {
    switch (id) {
      case 'full_name':
        return contactFullName || '';
      case 'email':
        return recipientObj.email || '';
      case 'company':
        return recipientObj.company || '';
      default:
        return '';
    }
  };

  // Lightweight in-box hint for signature/initials fields. Intentionally does NOT
  // print the signer name/email: that meta block was content-sized and inflated the
  // box beyond its stored geometry, so the placed field no longer matched what the
  // backend stamps (or what the signer sees). The signer identity still lives in the
  // property panel and the Signers modal. This hint mirrors the signer view exactly
  // ("Sign here" / "Initial here") and stretches to fill the box without forcing height.
  const renderSignHint = (box) => {
    const isInitials = box?.type === 'initials';
    const label = isInitials ? 'Initial here' : 'Sign here';
    // Signatures left-align to match the signer view and the stamped PDF; initials
    // stay centered in their square box.
    const alignClass = isInitials ? 'justify-center' : 'justify-start';
    return (
      <div className={`w-full h-full flex items-center ${alignClass} px-1 py-1 overflow-hidden`}>
        <span className="flex items-center gap-1 text-[11px] font-medium text-primary select-none">
          <span aria-hidden>✎</span>
          {label}
        </span>
      </div>
    );
  };

  // Double-click to place current tool
  useEffect(() => {
    if (!viewerRef.current) return;
    const container = viewerRef.current;

    const handleDblClick = (e) => {
      const pageLayer = e.target.closest('.rpv-core__page-layer');
      if (!pageLayer) return;

      const pageIndex = parseInt(pageLayer.dataset.virtualIndex, 10); // 0-based

      const rect = pageLayer.getBoundingClientRect();
      let offsetX = e.clientX - rect.left;
      let offsetY = e.clientY - rect.top;

      const baseWH = DEFAULT_RECT_BY_TYPE[currentTool];
      const width = baseWH.w;
      const height = baseWH.h;

      offsetX = Math.max(0, Math.min(offsetX, rect.width - width));
      offsetY = Math.max(0, Math.min(offsetY, rect.height - height));

      const id = `box-${Date.now()}`;

      const newBox = {
        id,
        pageIndex,   // important as on the reciver side we will be rendering based on page index
        leftRatio: offsetX / rect.width,
        topRatio: offsetY / rect.height,
        widthRatio: width / rect.width,
        heightRatio: height / rect.height,
        type: currentTool,
      };

      // defaults by type (fieldLabel on all standard fields)
      if (currentTool === 'text') {
        newBox.fieldLabel = '';
        newBox.textLabel = ''; // backward compatibility
        newBox.defaultText = '';
        newBox.placeholder = '';
      }
      if (currentTool === 'checkbox') {
        // Atomic checkbox: a single toggle overlaid on the box already printed
        // on the document. No label — the label lives on the PDF. Optional by
        // default so signers can check only the boxes that apply.
        newBox.required = false;
        newBox.defaultChecked = false;
      }
      if (currentTool === 'radio') {
        // Atomic radio button. Buttons that share a `group` are mutually
        // exclusive; place one over each printed option. No label — it's on the PDF.
        const group = 'group1';
        const countInGroup = boxes.filter(
          (b) => b.type === 'radio' && (b.group || 'group1') === group
        ).length;
        newBox.group = group;
        newBox.value = `option-${countInGroup + 1}`;
        newBox.defaultSelected = false;
        newBox.required = true;
      }
      if (currentTool === 'dropdown') {
        newBox.fieldLabel = '';
        newBox.dropdownOptions = [
          { id: uid(), label: 'Option A' }, // label-only
          { id: uid(), label: 'Option B' },
        ];
        newBox.dropdownDefault = toSnake('Option A'); // default by label
      }
      if (['date_signed', 'full_name', 'email', 'company'].includes(currentTool)) {
        const value = currentTool === 'date_signed' ? '' : getAutoValue(currentTool, recipientObj);
        newBox.fieldLabel = FIELD_TYPES[currentTool].label;
        newBox.defaultText = value;
        newBox.placeholder = FIELD_TYPES[currentTool].label;
      }
      if (SIGNABLE_FIELD_TYPES.has(currentTool)) {
        // OL-203: the field belongs to the signer picked in the Signers bar.
        newBox.signer_key = signers.some((s) => s.key === activeSignerKey) ? activeSignerKey : signers[0]?.key;
      }

      setBoxes((prev) => [...prev, newBox]);
      setSelectedBoxId(id);
    };

    container.addEventListener('dblclick', handleDblClick);
    return () => container.removeEventListener('dblclick', handleDblClick);
  }, [pdfUrl, currentTool, boxes, signers, activeSignerKey]);

  const updateBox = (id, updates) => {
    setBoxes((prev) => prev.map((box) => (box.id === id ? { ...box, ...updates } : box)));
  };

  const removeBox = (id) => {
    setBoxes((prev) => prev.filter((b) => b.id !== id));
    delete targetRefs.current[id];
    if (selectedBoxId === id) setSelectedBoxId(null);
  };

  const selectBox = (id) => setSelectedBoxId(id);

  const recipientObj = resolveRecipient(Data);

  // Preview Input on Document
  const renderFieldPreview = (box) => {
    switch (box.type) {
      case 'signature':
      case 'initials':
        return renderSignHint(box);
      case 'text':
        return (
          <div className="w-full h-full flex flex-col bg-transparent">
            <div className="px-2 pt-[2px] field-label-style">
              {box.fieldLabel || box.textLabel || ''}
            </div>
            <div className="px-2 pb-[4px]">
              <input
                className="w-full text-[11px] outline-none bg-transparent"
                value={box.defaultText || ''}
                readOnly
                disabled
                placeholder={box.placeholder || 'Type here...'}
              />
            </div>
          </div>
        );
      case 'checkbox':
        return (
          <div className="w-full h-full flex items-center justify-center bg-transparent">
            <input type="checkbox" checked={!!box.defaultChecked} readOnly disabled />
          </div>
        );
      case 'radio':
        return (
          <div className="w-full h-full flex items-center justify-center bg-transparent">
            <input
              type="radio"
              name={(box.group || 'group') + '-' + box.id}
              checked={!!box.defaultSelected}
              readOnly
              disabled
            />
          </div>
        );
      case 'dropdown':
        return (
          <div className="w-full h-full flex flex-col bg-transparent text-[11px]">
            <div className="px-2 pt-[2px] field-label-style">{box.fieldLabel || ''}</div>
            <div className="px-2 pb-[4px]">
              <select className="w-full bg-transparent text-black" value={box.dropdownDefault || ''} onChange={() => { }} disabled>
                {(box.dropdownOptions || []).map((opt) => {
                  const val = toSnake(opt.label || '');
                  return (
                    <option key={opt.id} value={val}>
                      {opt.label}
                    </option>
                  );
                })}
              </select>
            </div>
          </div>
        );
      case 'date_signed':
      case 'full_name':
      case 'email':
      case 'company':
        return (
          <div className="w-full h-full flex flex-col bg-transparent">
            <div className="px-2 pt-[2px] field-label-style">
              {box.fieldLabel || ''}
              {/* FIELD_TYPES[box.type].label */}
            </div>
            <div className="px-2 pb-[4px]">
              <input
                className="w-full text-[11px] outline-none bg-transparent"
                value={box.defaultText || ''}
                onChange={(e) => {
                  if (box.isEditable) updateBox(box.id, { defaultText: e.target.value });
                }}
                readOnly={!box.isEditable}
                disabled={!box.isEditable}
                placeholder={box.placeholder || FIELD_TYPES[box.type].label}
              />
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  // Render Boxes on document
  const renderBoxes = () =>
    boxes.map((box) => {
      // Updated selector to get the virtualized page based on current page index
      const pageElement = document.querySelector(
        `.rpv-core__page-layer[data-virtual-index="${box.pageIndex}"]`
      );
      if (!pageElement) return null;

      const rect = pageElement.getBoundingClientRect();

      const top = box.topRatio * rect.height;
      const left = box.leftRatio * rect.width;
      const width = box.widthRatio * rect.width;
      const height = box.heightRatio * rect.height;

      const isSelected = selectedBoxId === box.id;
      // Signature and initials take their signer's colour and number (OL-203).
      const signerIndex = SIGNABLE_FIELD_TYPES.has(box.type) ? signers.findIndex((s) => s.key === box.signer_key) : -1;
      const fieldColor = signerIndex >= 0 ? signerColor(signerIndex) : null;

      return ReactDOM.createPortal(
        <div
          key={box.id}
          data-box-id={box.id}
          ref={(el) => {
            if (el) {
              targetRefs.current[box.id] = el;
            }
          }}
          onMouseDown={() => selectBox(box.id)}
          onPointerDown={() => selectBox(box.id)}
          onTouchStart={() => selectBox(box.id)}
          style={{
            position: 'absolute',
            top,
            left,
            // Explicit width/height (not min*) so the box is exactly the geometry
            // that gets stored and stamped — true WYSIWYG. Previously height was
            // 'fit-content', so in-box content (the old signer name/email block)
            // inflated the box beyond its stored ratio and the placed field no
            // longer matched the signed result.
            width,
            height,
            color: 'text-black',
            backgroundColor: fieldColor ? `${fieldColor}26` : 'var(--tw-primary-clarity)',
            border: `2px solid ${fieldColor || 'var(--tw-primary)'}`,
            boxShadow: isSelected ? '0 0 0 2px rgba(59,130,246,0.5)' : 'none',
            zIndex: 999,
            cursor: 'move',
            borderRadius: 6,
            overflow: 'hidden',
          }}
        >
          <span
            className="h-[18px] flex items-center justify-center absolute top-0 left-0 bg-primary text-white rounded-md !rounded-t-none !rounded-bl-none text-[10px] px-1"
            style={fieldColor ? { backgroundColor: fieldColor } : undefined}
          >
            {FIELD_TYPES[box.type]?.badge || 'Field'}{signerIndex >= 0 ? ` · ${signerIndex + 1}` : ''}
          </span>
          <button
            className="absolute top-0 right-0 p-1 md:px-1 text-sm text-red-600 bg-white z-[999] rounded-bl shadow-sm"
            onClick={(e) => {
              e.stopPropagation();
              removeBox(box.id);
            }}
            onTouchStart={(e) => e.stopPropagation()}
            onTouchEnd={(e) => {
              e.stopPropagation();
              e.preventDefault();
              removeBox(box.id);
            }}
            onPointerDown={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
            type="button"
            title="Remove"
          >
            <KeenIcon icon="cross" className="md:size-auto size-4" />
          </button>
          <div className="w-full h-full pt-[18px]">{renderFieldPreview(box)}</div>
        </div>,
        pageElement
      );
    });

  const renderMoveables = () =>
    boxes.map((box) => {
      const pageElement = document.querySelector(
        `.rpv-core__page-layer[data-virtual-index="${box.pageIndex}"]`
      );
      if (!pageElement) return null;

      // look up fresh target every render
      const target = pageElement.querySelector(`[data-box-id="${box.id}"]`);
      if (!target) return null;

      return ReactDOM.createPortal(
        <Moveable
          key={box.id + '-' + currentScale}
          target={target}
          container={pageElement}
          rootContainer={pageElement}
          draggable
          resizable
          updateTarget={true}
          keepRatio={false}
          edge={true}
          throttleResize={1}
          renderDirections={['nw', 'ne', 'sw', 'se', 'n', 's', 'e', 'w']}
          zoom={currentScale}
          onDrag={({ left, top }) => {
            const rect = pageElement.getBoundingClientRect();
            const boxW = box.widthRatio * rect.width;
            const boxH = box.heightRatio * rect.height;

            const clampedLeft = Math.max(0, Math.min(left, rect.width - boxW));
            const clampedTop = Math.max(0, Math.min(top, rect.height - boxH));

            updateBox(box.id, {
              leftRatio: clampedLeft / rect.width,
              topRatio: clampedTop / rect.height,
            });
          }}
          onResize={({ target, width, height, drag }) => {
            const rect = pageElement.getBoundingClientRect();

            // Apply size directly to DOM
            target.style.width = `${width}px`;
            target.style.height = `${height}px`;
            target.style.left = `${drag.left}px`;
            target.style.top = `${drag.top}px`;

            // Clamp
            let newW = Math.max(28, Math.min(width, rect.width - drag.left));
            let newH = Math.max(22, Math.min(height, rect.height - drag.top));

            updateBox(box.id, {
              widthRatio: newW / rect.width,
              heightRatio: newH / rect.height,
              leftRatio: drag.left / rect.width,
              topRatio: drag.top / rect.height,
            });
          }}
        />,
        pageElement
      );
    });

  const handleCloseModal = () => {
    setPdfUrl(null);
    setBoxes([]);
    setUploadedFile(null);
    setSelectedBoxId(null);
    setSigners([recipientSigner()]);
    setActiveSignerKey(RECIPIENT_KEY);
    clearAllLocalErrors();
    onClose();
  };

  const removeFile = () => {
    setUploadedFile(null);
    setPdfUrl(null);
    setDropAreaError('');
    setBoxes([]);
    setSelectedBoxId(null);
  };


  if (!show) return null;

  const combinedErrors = { ...errors, ...uiErrors };
  const selectedBox = boxes.find((b) => b.id === selectedBoxId) || null;
  const signerContext = { recipient: recipientObj, forMany: forMultipleContacts };

  // Final Payload sent to API
  const buildPayload = (contact_id = [], message, subject, due_date = '') => {
    // return console.log(message, subject);

    const normalized = boxes.map((box) => {
      if (
        ['date_signed', 'full_name', 'email', 'company'].includes(box.type)
      ) {
        box = { ...box, type: 'text' };
      }
      console.log('box', box.type);

      const base = {
        id: box.id,
        type: box.type,
        page: box.pageIndex,
        // page: box.pageNumber ?? (box.pageIndex + 1),
        // page: [...document.querySelectorAll('.rpv-core__page-layer')].indexOf(box.pageElement),
        left: box.leftRatio,
        top: box.topRatio,
        width: box.widthRatio,
        height: box.heightRatio,

        // unified label for standard fields (+ keep textLabel for back-compat)
        fieldLabel: box.fieldLabel ?? (box.textLabel ?? undefined),

        // text
        textLabel: box.textLabel ?? undefined,
        defaultText: box.defaultText ?? undefined,
        placeholder: box.placeholder ?? undefined,

        // checkbox (atomic) -> a single toggle, no label
        required: box.type === 'checkbox' || box.type === 'radio' ? box.required !== false : undefined,
        defaultChecked: box.type === 'checkbox' ? !!box.defaultChecked : undefined,

        // radio (atomic) -> individually placed buttons grouped by `group`
        group: box.group ?? undefined,
        value: box.type === 'radio' ? (box.value ?? '') : undefined,
        defaultSelected: box.type === 'radio' ? !!box.defaultSelected : undefined,

        // dropdown -> value derived from label; default is snake(label)
        dropdownOptions: (box.dropdownOptions || []).map((opt) => {
          const label = opt.label || '';
          return { id: opt.id, label, value: toSnake(label) };
        }) || undefined,
        dropdownDefault:
          box.dropdownDefault ||
          ((box.dropdownOptions && box.dropdownOptions.length)
            ? toSnake(box.dropdownOptions[0].label || '')
            : undefined),
      };

      if (SIGNABLE_FIELD_TYPES.has(box.type)) {
        base.signer = signerPayload(signers, box.signer_key, signerContext);
      }

      return base;
    });

    return {
      contact_id: contact_id,
      email_subject: subject,
      message: message,
      due_date: due_date || '',
      file: uploadedFile,
      fields: normalized,
    };
  };

  // Field Property panel helpers 
  const ToolButton = ({ tool, Icon }) => (
    <button
      type="button"
      className={`btn btn-xs ${currentTool === tool ? 'btn-primary' : 'btn-light'}`}
      onClick={() => setCurrentTool(tool)}
      title={FIELD_TYPES[tool].label}
    >
      <Icon className="w-3.5 h-3.5" />
      <span className="ml-1 hidden sm:inline">{FIELD_TYPES[tool].label}</span>
    </button>
  );

  // add/edit/remove item helper for arrays
  const addArrayItem = (key, def) => {
    if (!selectedBox) return;
    const arr = selectedBox[key] || [];
    updateBox(selectedBox.id, { [key]: [...arr, { id: uid(), ...def }] });
  };

  const updateArrayItem = (key, itemId, patch) => {
    if (!selectedBox) return;
    const curr = selectedBox[key] || [];
    const prevItem = curr.find((it) => it.id === itemId);
    const arr = curr.map((it) => (it.id === itemId ? { ...it, ...patch } : it));
    const updates = { [key]: arr };

    // keep dropdownDefault in sync if the default option's label changes
    if (key === 'dropdownOptions' && 'label' in patch && prevItem) {
      const prevSlug = toSnake(prevItem.label || '');
      if ((selectedBox.dropdownDefault || '') === prevSlug) {
        updates.dropdownDefault = toSnake(patch.label || '');
      }
    }
    updateBox(selectedBox.id, updates);
  };

  const removeArrayItem = (key, itemId) => {
    if (!selectedBox) return;
    const arr = (selectedBox[key] || []).filter((it) => it.id !== itemId);

    const updates = { [key]: arr };
    // if removing the dropdown default option, shift default to first available
    if (key === 'dropdownOptions') {
      const prevDefault = selectedBox.dropdownDefault || '';
      const removedWasDefault = (selectedBox.dropdownOptions || []).some(
        (opt) => opt.id === itemId && toSnake(opt.label || '') === prevDefault
      );
      if (removedWasDefault) {
        const first = arr[0];
        updates.dropdownDefault = first ? toSnake(first.label || '') : '';
      }
    }

    updateBox(selectedBox.id, updates);
  };

  const closeFileUploadModal = () => {
    setDocUploadModal(false);
  }

  const handleOpenSendModal = () => {
    const problem = signerProblem(signers, boxes, signerContext);
    if (problem) {
      setUiErrors({ signer_fields: problem });
      return;
    }

    setUiErrors({});
    setDocUploadModal(true);
  };

  return (
    <div className="modal-wrapper modal-animate-fade">
      <div className={`modal-body modal-animate-scale max-h-[97vh] !p-0 !px-5 !pb-5 ${uploadedFile ? 'container-fixed mx-2.5' : 'sm:w-4/5 w-[95%] max-w-xl'}`}>
        <div className='sticky top-0 z-10 flex justify-between items-center bg-[--tw-navbar-bg] dark:bg-[--tw-navbar-bg-dark] pt-5 pb-1'>
          <h3 className="capitalize card-title">{title}</h3>
          <button onClick={handleCloseModal} className="modal-close-btn-normal" type="button">
            ×
          </button>
        </div>

        {!pdfUrl ? (
          <div
            {...getRootProps()}
            className={`group hover:border-primary hover:bg-primary-light border-2 border-dashed my-5 p-5 h-[150px] flex items-center justify-center flex-col text-sm rounded-md cursor-pointer transition ${isDragActive ? 'border-primary bg-primary-light' : 'border-gray-300'
              }`}
          >
            <input
              {...getInputProps()}
              onChange={(e) => {
                handleSelectedFiles(Array.from(e.target.files || []));
                e.target.value = '';
              }}
            />
            {/* <p>{isDragActive ? 'Drop the PDF here...' : 'Drag & drop your PDF file here, or click to select'}</p>
            <p className="mt-1 text-xs text-gray-400">Only .pdf files are supported • Max size {FormatFileSize(MAX_SIZE)}</p> */}
            <KeenIcon icon="file-up" className="group-hover:text-primary group-hover:scale-110 transition-all text-gray-500 text-3xl mb-2" />
            {combiningCount > 0 ? (
              <p>Combining {combiningCount} PDFs into one signing packet...</p>
            ) : isDragActive ? (
              <p>Drop the PDFs here ...</p>
            ) : (
              <p className='group-hover:text-primary-active'>Drag & drop your PDF files here, or click to select</p>
            )}
            <p className="mt-1 text-xs text-gray-400 group-hover:text-gray-600">Only .pdf files are supported • Max size {FormatFileSize(MAX_SIZE)} in total</p>
            <p className="mt-1 text-xs text-gray-400 group-hover:text-gray-600">Pick several PDFs to send them as one packet: place the fields once, and signers sign once.</p>
            {dropAreaError && <p className="mt-1 text-xs text-red-600">{dropAreaError}</p>}
          </div>
        ) : (
          <>
            {/* FILE STRIP */}
            <div className="flex flex-wrap items-center justify-between p-2 mt-1 border rounded-md border-primary bg-primary-light">
              <p className="text-sm font-medium">
                {uploadedFile?.name} <span className="text-gray-500">({uploadedFile ? FormatFileSize(uploadedFile.size) : ''})</span>
              </p>
              <div className="flex items-center gap-2">
                <button type="button" onClick={removeFile} className="text-xs underline text-danger">
                  Remove
                </button>
              </div>
            </div>

            {/* GUIDANCE BANNER */}
            <div className="flex items-start gap-2 p-3 my-2.5 border border-blue-200 bg-blue-50 text-blue-900 rounded-md">
              <BadgeInfo className="w-5 h-5 mt-0.5 shrink-0 text-blue-600" />
              <div className="text-xs leading-relaxed">
                <div className="font-semibold mb-1">How to place fields on the document:</div>
                <ol className="list-decimal ml-4 space-y-0.5">
                  <li>Under <span className="font-semibold">Signers</span>, add everyone who needs to sign, in signing order.</li>
                  <li>Click a signer, pick a field type below (Signature, Initials, Date, etc.), then <span className="font-semibold">double-click</span> the PDF where it goes.</li>
                  <li>Drag the field to reposition it, or use the corner handles to resize.</li>
                  <li>Click a placed field to edit its properties (signer, label, options) in the side panel.</li>
                  <li>Once every signer has a field, click <span className="font-semibold">{forMultipleContacts ? 'Send to Multiple Contacts' : 'Send'}</span>.</li>
                </ol>
              </div>
            </div>

            <EsignSignersBar
              signers={signers}
              activeKey={activeSignerKey}
              fieldCounts={signerFieldCounts(boxes)}
              recipient={recipientObj}
              forMany={forMultipleContacts}
              previousSigners={signerOptions.filter((opt) => {
                // Don't offer someone who is already signing this document.
                const email = String(opt.signer_email || '').trim().toLowerCase();
                const taken = signers.map((s) => (s.role === 'recipient' && !forMultipleContacts ? recipientObj.email : s.email));
                return email && !taken.some((t) => String(t || '').trim().toLowerCase() === email);
              })}
              onSelect={(key) => {
                setActiveSignerKey(key);
                if (!SIGNABLE_FIELD_TYPES.has(currentTool)) setCurrentTool('signature');
              }}
              onAdd={addSigner}
              onUpdate={updateSigner}
              onRemove={removeSigner}
              onMove={(key, delta) => setSigners((prev) => moveSigner(prev, key, delta))}
            />

            {/* TOOLBAR (grouped) */}
            <div className="flex flex-col gap-2 my-2.5">
              {/* <div className="w-full flex items-center justify-between">
                <Alert variant="warning" className="text-start !p-2 !text-xs" icon="information">
                  <div className="!text-xs">Select a field type, then double-click on a PDF page to place it.</div>
                </Alert>
                <div className="ml-auto">
                  <ZoomIn>
                    {(props) => (
                      <button type="button" className="btn btn-xs btn-light" onClick={props.onClick} disabled={props.isDisabled}>
                        <KeenIcon icon="plus" />
                      </button>
                    )}
                  </ZoomIn>
                  <CurrentScale>
                    {(props) => {
                      useEffect(() => setCurrentScale(props.scale), [props.scale]);
                      return <span className="text-xs px-2"> {Math.round(props.scale * 100)}%</span>;
                    }}
                  </CurrentScale>
                  <ZoomOut>
                    {(props) => (
                      <button type="button" className="btn btn-xs btn-light" onClick={props.onClick} disabled={props.isDisabled}>
                        <KeenIcon icon="minus" />
                      </button>
                    )}
                  </ZoomOut>
                </div>
              </div> */}

              <div className="flex flex-wrap items-center justify-between gap-2">
                <div>
                  <div className="text-xs font-semibold pr-1 mb-1">Signature Fields:</div>
                  <div className="flex items-center flex-wrap gap-1.5">
                    <ToolButton tool="signature" Icon={Signature} />
                    <ToolButton tool="initials" Icon={CaseUpper} />
                  </div>
                </div>

                <div>
                  <div className="text-xs font-semibold pr-1 mb-1">Auto Fields:</div>
                  <div className="flex items-center flex-wrap gap-1.5">
                    <ToolButton tool="date_signed" Icon={Calendar1} />
                    {!forMultipleContacts && (
                      <>
                        <ToolButton tool="full_name" Icon={ALargeSmall} />
                        <ToolButton tool="email" Icon={Mail} />
                      </>
                    )}
                    <ToolButton tool="company" Icon={Building2} />
                  </div>
                </div>

                <div>
                  <div className="text-xs font-semibold pr-1 mb-1">Standard Fields:</div>
                  <div className="flex items-center flex-wrap gap-1.5">
                    <ToolButton tool="text" Icon={Type} />
                    <ToolButton tool="checkbox" Icon={CheckSquare} />
                    <ToolButton tool="radio" Icon={CheckCircle} />
                    <ToolButton tool="dropdown" Icon={ChevronsUpDown} />
                  </div>
                </div>
              </div>
            </div>

            {/* MAIN AREA */}
            <div className="flex items-center justify-between gap-3">
              {/* PDF Render */}
              <div className={`${togglePropertyPanel ? 'w-full' : 'lg:w-[75%] w-full'} slide-transition relative`}>
                {togglePropertyPanel && (
                  <DefaultTooltip title="Expand Property Panel">
                    <div
                      onClick={togglePropertyPannel}
                      className='cursor-pointer absolute top-1/2 z-10 -right-4 size-8 flex items-center justify-center rounded-full bg-light border border-primary-clarity'>
                      <KeenIcon icon="exit-left" />
                    </div>
                  </DefaultTooltip>
                )}
                <div className="w-full max-w-[95%] mx-auto flex items-center justify-between absolute top-2 left-1/2 -translate-x-1/2 z-[1]">
                  <div className="relative flex items-center group">
                    <div className="flex items-center justify-center size-7 rounded-full bg-white border border-info text-info shadow-sm cursor-pointer animate-pulse group-hover:animate-none transition-all duration-300">
                      <KeenIcon icon="information" className="size-4" />
                    </div>

                    {/* EXPANDING MESSAGE */}
                    <div className="absolute left-10 top-1/2 -translate-y-1/2 bg-white border border-info text-gray-900 text-xs px-3 py-1.5 rounded-md shadow-md whitespace-nowrap transition-all duration-300 origin-left opacity-0 scale-95 -translate-x-3 group-hover:opacity-100 group-hover:scale-100 group-hover:translate-x-0">
                      Select a field type, then double-click on a PDF page to place it.
                    </div>
                  </div>

                  <div className="bg-info-clarity border border-info p-1 rounded-md flex items-center">
                    <ZoomIn>
                      {(props) => (
                        <button type="button" className="badge badge-sm badge-info" onClick={props.onClick} disabled={props.isDisabled}>
                          <KeenIcon icon="plus" />
                        </button>
                      )}
                    </ZoomIn>
                    <CurrentScale>
                      {(props) => {
                        useEffect(() => setCurrentScale(props.scale), [props.scale]);
                        return <span className="text-xs px-2"> {Math.round(props.scale * 100)}%</span>;
                      }}
                    </CurrentScale>
                    <ZoomOut>
                      {(props) => (
                        <button type="button" className="badge badge-sm badge-info" onClick={props.onClick} disabled={props.isDisabled}>
                          <KeenIcon icon="minus" />
                        </button>
                      )}
                    </ZoomOut>
                  </div>
                </div>
                <div ref={viewerRef} className="h-[70vh] overflow-auto border rounded relative">
                  <style>{`
                    @media (max-width: 768px) {
                      .rpv-core__tooltip-body { 
                        display: none !important; 
                      }
                      .rpv-core__tooltip { 
                        display: none !important; 
                      }
                      .moveable-control {
                        width: 24px !important;
                        height: 24px !important;
                        margin-top: -12px !important;
                        margin-left: -12px !important;
                      }
                      .moveable-control.moveable-n, 
                      .moveable-control.moveable-s, 
                      .moveable-control.moveable-e, 
                      .moveable-control.moveable-w {
                         /* Just keep them slightly more prominent on mobile */
                         width: 20px !important;
                         height: 20px !important;
                         margin-top: -10px !important;
                         margin-left: -10px !important;
                      }
                    }
                  `}</style>
                  <Worker workerUrl={PDF_WORKER_URL}>
                    <Viewer fileUrl={pdfUrl} defaultScale={1} plugins={[zoomPluginInstance]} />
                  </Worker>
                  {renderBoxes()}
                  {renderMoveables()}
                </div>

                {Object.entries(combinedErrors).map(([field, message]) => (
                  <div key={field} className="mt-2 text-xs text-red-500">
                    {message}
                  </div>
                ))}
              </div>

              {/* PROPERTY PANEL */}
              <div className={`${togglePropertyPanel ? 'w-0 overflow-hidden' : 'lg:w-[25%] md:w-1/2 w-[75%]'
                } slide-transition absolute right-0 z-[2]`}>
                <DefaultTooltip title="Collapse Property Panel">
                  <div
                    onClick={togglePropertyPannel}
                    className='cursor-pointer absolute top-1/2 z-9 -left-4 size-8 flex items-center justify-center rounded-full bg-light border border-primary-clarity'>
                    <KeenIcon icon="exit-right" />
                  </div>
                </DefaultTooltip>
                <div className="border rounded p-3 h-[70vh] overflow-auto bg-gray-100">
                  <div className="font-semibold mb-2">Field Properties Panel</div>
                  {!selectedBox ? (
                    <div className="w-full h-[90%] flex flex-col justify-center items-center">
                      <BadgeInfo className="size-12" />
                      <div className='text-center'>
                        <h5 className="text-lg mt-1 text-gray-500 font-medium">Nothing Selected!</h5>
                        <p className="text-sm text-gray-500">Select a field to edit its properties.</p>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="text-xs mb-2">
                        <div>
                          <span className="font-semibold">Field Type:</span>
                          <span className="badge badge-sm badge-primary badge-outline ml-1">
                            {FIELD_TYPES[selectedBox.type].label}
                          </span>
                        </div>
                      </div>

                      {/* Signature / Initials: who signs this field (OL-203) */}
                      {SIGNABLE_FIELD_TYPES.has(selectedBox.type) && (
                        <div className="mt-2 bg-info-light border border-info p-1.5 rounded-md">
                          <label className="block text-xs">
                            <span className="font-semibold">Signed by</span>
                            <select
                              className="select select-sm w-full mt-1"
                              value={selectedBox.signer_key || ''}
                              onChange={(e) => {
                                updateBox(selectedBox.id, { signer_key: e.target.value });
                                setUiErrors({});
                              }}
                            >
                              {signers.map((signer, index) => (
                                <option key={signer.key} value={signer.key}>
                                  {index + 1}. {signerDisplay(signer, signerContext).name}
                                </option>
                              ))}
                            </select>
                          </label>
                          <p className="text-[11px] text-gray-500 mt-1 leading-snug">
                            Add people, or change the signing order, in the Signers bar above the document.
                          </p>
                        </div>
                      )}

                      {/* TEXTBOX */}
                      {selectedBox.type === 'text' && (
                        <div className="bg-info-light border border-info p-1.5 rounded-md">
                          <label className="block text-xs mt-2">
                            <span className='flex items-center justify-between'>
                              Field label:
                              <button className='text-xs underline text-danger'
                                onClick={() => { updateBox(selectedBox.id, { fieldLabel: '', textLabel: '' }) }}>Clear Label</button>
                            </span>
                            <input
                              className="input input-xs w-full mt-1"
                              value={selectedBox.fieldLabel ?? selectedBox.textLabel ?? ''}
                              onChange={(e) =>
                                updateBox(selectedBox.id, { fieldLabel: e.target.value, textLabel: e.target.value })
                              }
                              placeholder="Type your label here..."
                            />
                          </label>
                          {/* <label className="block text-xs mt-2">
                            <DefaultTooltip title="If a value is entered, it will be pre-filled for the recipient. Leave it blank to send an empty field.">
                              <div className="flex items-center gap-0.5">
                                <KeenIcon icon="information" />
                                Value:
                              </div>
                            </DefaultTooltip>
                            <input
                              className="input input-xs w-full mt-1"
                              value={selectedBox.defaultText || ''}
                              onChange={(e) => updateBox(selectedBox.id, { defaultText: e.target.value })}
                              placeholder="e.g., Yes, I’m available..."
                            />
                          </label> */}
                          <label className="block text-xs mt-2">
                            Placeholder:
                            <input
                              className="input input-xs w-full mt-1"
                              value={selectedBox.placeholder || ''}
                              onChange={(e) => updateBox(selectedBox.id, { placeholder: e.target.value })}
                              placeholder="Type here..."
                            />
                          </label>
                        </div>
                      )}

                      {/* CHECKBOX (atomic) - no label, overlays the printed box */}
                      {selectedBox.type === 'checkbox' && (
                        <div className="space-y-2 mt-2">
                          <p className="text-[11px] text-gray-500 leading-snug">
                            Place this checkbox over the box already printed on the
                            document. The signer just checks it — no label needed.
                          </p>
                          <label className="flex items-center gap-2 text-xs">
                            <input
                              type="checkbox"
                              className="checkbox checkbox-sm"
                              checked={!!selectedBox.defaultChecked}
                              onChange={(e) => updateBox(selectedBox.id, { defaultChecked: e.target.checked })}
                            />
                            Checked by default
                          </label>
                          <label className="flex items-center gap-2 text-xs">
                            <input
                              type="checkbox"
                              className="checkbox checkbox-sm"
                              checked={selectedBox.required !== false}
                              onChange={(e) => updateBox(selectedBox.id, { required: e.target.checked })}
                            />
                            Required
                          </label>
                        </div>
                      )}

                      {/* RADIO (atomic, grouped) - no label, overlays the printed circle */}
                      {selectedBox.type === 'radio' && (
                        <div className="space-y-2 mt-2">
                          <p className="text-[11px] text-gray-500 leading-snug">
                            Place one radio button over each printed option. Buttons
                            sharing a group are mutually exclusive — the signer can
                            pick only one per group.
                          </p>
                          <label className="block text-xs">
                            Group
                            <input
                              className="input input-xs w-full mt-1"
                              value={selectedBox.group || ''}
                              onChange={(e) => updateBox(selectedBox.id, { group: e.target.value })}
                              placeholder="e.g., contact-method"
                            />
                          </label>
                          <label className="block text-xs">
                            Value (recorded when this option is selected)
                            <input
                              className="input input-xs w-full mt-1"
                              value={selectedBox.value || ''}
                              onChange={(e) => updateBox(selectedBox.id, { value: e.target.value })}
                              placeholder="e.g., email"
                            />
                          </label>
                          <label className="flex items-center gap-2 text-xs">
                            <input
                              type="checkbox"
                              className="checkbox checkbox-sm"
                              checked={!!selectedBox.defaultSelected}
                              onChange={(e) => updateBox(selectedBox.id, { defaultSelected: e.target.checked })}
                            />
                            Selected by default
                          </label>
                          <label className="flex items-center gap-2 text-xs">
                            <input
                              type="checkbox"
                              className="checkbox checkbox-sm"
                              checked={selectedBox.required !== false}
                              onChange={(e) => updateBox(selectedBox.id, { required: e.target.checked })}
                            />
                            Required
                          </label>
                        </div>
                      )}

                      {/* DROPDOWN (options editor) - label only */}
                      {selectedBox.type === 'dropdown' && (
                        <>
                          <label className="block text-xs mt-2">
                            <span className='flex items-center justify-between'>
                              Field label:
                              <button className='text-xs underline text-danger'
                                onClick={() => { updateBox(selectedBox.id, { fieldLabel: '' }) }}>Clear Label</button>
                            </span>
                            <input
                              className="input input-xs w-full mt-1"
                              value={selectedBox.fieldLabel || ''}
                              onChange={(e) => updateBox(selectedBox.id, { fieldLabel: e.target.value })}
                              placeholder="Type your label here..."
                            />
                          </label>

                          <div className="text-xs font-semibold mt-2 mb-1">Options</div>
                          <div className="space-y-2">
                            {(selectedBox.dropdownOptions || []).map((opt) => (
                              <div key={opt.id} className="flex items-stretch gap-1 bg-info-light border border-info p-1.5 rounded-md">
                                <div className="w-full">
                                  <label className="form-label mb-1 text-xs">Label:</label>
                                  <input
                                    className="input input-sm w-full"
                                    value={opt.label || ''}
                                    onChange={(e) =>
                                      updateArrayItem('dropdownOptions', opt.id, { label: e.target.value })
                                    }
                                    placeholder="Type here..."
                                  />
                                </div>
                                <div>
                                  <label className="form-label mb-1 text-xs invisible">.</label>
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-danger btn-outline"
                                    onClick={() => removeArrayItem('dropdownOptions', opt.id)}
                                    title="Remove"
                                  >
                                    <KeenIcon icon="trash" />
                                  </button>
                                </div>
                              </div>
                            ))}
                          </div>
                          <button
                            type="button"
                            className="btn btn-xs mt-2 btn-link text-xs ml-auto leading-3"
                            onClick={() => addArrayItem('dropdownOptions', { label: 'New Option' })}
                          >
                            <KeenIcon icon="plus" /> Add option
                          </button>

                          <label className="block text-xs mt-3">
                            <DefaultTooltip title="Select an option from the list to set it as the default value for the dropdown.">
                              <div className="flex items-center gap-0.5">
                                <KeenIcon icon="information" />
                                Default value
                              </div>
                            </DefaultTooltip>
                            <select
                              className="input input-xs w-full mt-1"
                              value={selectedBox.dropdownDefault || ''}
                              onChange={(e) => updateBox(selectedBox.id, { dropdownDefault: e.target.value })}
                            >
                              {(selectedBox.dropdownOptions || []).map((opt) => {
                                const val = toSnake(opt.label || '');
                                return (
                                  <option key={opt.id} value={val}>
                                    {opt.label} ({val})
                                  </option>
                                );
                              })}
                            </select>
                          </label>
                        </>
                      )}

                      {['date_signed', 'full_name', 'email', 'company'].includes(selectedBox.type) && (
                        <div className="bg-info-light border border-info p-1.5 rounded-md">
                          <label className="block text-xs mt-2">
                            <span className='flex items-center justify-between'>
                              Field label:
                              <button className='text-xs underline text-danger'
                                onClick={() => { updateBox(selectedBox.id, { fieldLabel: '', textLabel: '' }) }}>Clear Label</button>
                            </span>
                            <input
                              className="input input-xs w-full mt-1"
                              value={selectedBox.fieldLabel || ''}
                              onChange={(e) => updateBox(selectedBox.id, { fieldLabel: e.target.value })}
                              placeholder={FIELD_TYPES[selectedBox.type].label}
                            />
                          </label>

                          <label className="block text-xs mt-2">
                            <DefaultTooltip title="Edit the pre-filled value. This will be shown to the recipient as the default value but can be changed.">
                              <div className="flex items-center gap-0.5">
                                <KeenIcon icon="information" />
                                Value:
                              </div>
                            </DefaultTooltip>
                            <input
                              className="input input-xs w-full mt-1"
                              value={selectedBox.defaultText || ''}
                              onChange={(e) => updateBox(selectedBox.id, { defaultText: e.target.value })}
                              placeholder={FIELD_TYPES[selectedBox.type].label}
                              disabled={selectedBox.type === 'date_signed'}
                            />
                          </label>

                          <label className="block text-xs mt-2">
                            Placeholder:
                            <input
                              className="input input-xs w-full mt-1"
                              value={selectedBox.placeholder || ''}
                              onChange={(e) => updateBox(selectedBox.id, { placeholder: e.target.value })}
                              placeholder="Type here..."
                            />
                          </label>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* ACTIONS */}
            <div className="flex justify-end gap-2 mt-4">
              <button
                disabled={!boxes.length || !uploadedFile || loading}
                // Show email subj/body modal
                onClick={handleOpenSendModal}
                // onClick={() => onConfirm(buildPayload())}
                className="btn btn-sm btn-primary"
                type="button"
              >
                {loading ? 'Please Wait...' : forMultipleContacts ? 'Send to Multiple Contacts' : `Send`}
                {/* {loading ? 'Please Wait...' : forMultipleContacts ? 'Send to Multiple Contacts' : `Send to ${contactFullName}`} */}
              </button>
              <button onClick={handleCloseModal} className="btn btn-sm btn-outline btn-danger" type="button">
                Cancel
              </button>
            </div>
          </>
        )}
      </div>

      {/* Ask Email Subj/Body Before sending to recipient */}
      <RequestFileUploadModal
        open={docUploadModal}
        title="Send Document For Signature"
        initialValues={{ contact_id: [], message: "", subject: "", due_date: "" }}
        validationSchema={validationSchema}
        onSubmit={({ contact_id, message, subject, due_date }) => onConfirm(buildPayload(contact_id, message, subject, due_date))}
        onClose={closeFileUploadModal}
        loading={loading}
        showPatientSelection={forMultipleContacts}
        forMultipleContacts={true}
        patientFieldLabel="Contacts"
        patientPlaceholder="Pick one or more contacts"
        patientFieldHint="Each contact gets their own copy to sign, in a separate email. Only contacts with an email address are listed."
        fields={[
          {
            name: "subject",
            label: "Email Subject",
            type: "text",
            optional: false,
            showCounter: true,
            max: 100,
            required: true
          },
          {
            name: "message",
            label: "Email Body",
            type: "textarea",
            rows: 6,
            optional: true,
            showCounter: true,
            max: 1000,
            required: false
          },
          {
            // OL-205: shown in the email and in tracking; the link stays open until then.
            name: "due_date",
            label: "Sign By",
            type: "date",
            optional: true,
            required: false,
            // The server refuses dates more than a year out (links last until then).
            inputProps: { min: yyyymmdd(), max: yyyymmdd(new Date(new Date().setFullYear(new Date().getFullYear() + 1))) },
          },
        ]}
      />
    </div>
  );
};

export { DocumentUploadModal };
